<template>
    <div class="weekday-time-selector">
        <div class="selector-header">
            <h4>Настройки времени по дням недели</h4>
        </div>

        <div class="weekdays-container">
            <div v-for="(dayName, dayNumber) in availableDaysOfWeek" :key="dayNumber" class="weekday-section has-settings">
                <div class="weekday-header">
                    <div class="weekday-name">
                        <span class="day-label">
                            {{ dayName }}
                        </span>
                        <span class="day-multiplier" v-if="getDayMultiplier(dayNumber) > 1">
                            x{{ getDayMultiplier(dayNumber) }}
                        </span>
                    </div>

                    <!-- Визуализация часов в заголовке -->
                    <div class="hour-grid-container">
                        <!-- Подсказка с часами -->
                        <div class="hour-labels">
                            <div v-for="hour in 24" :key="`label-${hour}`" class="hour-label">
                                {{ (hour - 1).toString().padStart(2, '0') }}
                            </div>
                        </div>
                        <!-- Чекбоксы -->
                        <div class="hour-grid">
                            <div v-for="hour in 24" :key="hour" class="hour-checkbox" :class="{
                                active: isHourActiveForDay(dayNumber, hour),
                                disabled: !isHourAllowedForDay(dayNumber, hour)
                            }" @click="toggleHourForDay(dayNumber, hour)"
                                :title="getHourTooltip(dayNumber, hour)">
                                <div class="checkbox-square">
                                    <div class="square-fill"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="day-summary">
                        <span class="total-hours">
                            {{ getTotalHoursForDay(dayNumber) }}ч
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="global-summary">
            <div class="summary-row">
                <span class="summary-label">Всего дней:</span>
                <span class="summary-value">{{ getActiveDaysCount() }}</span>
            </div>
            <div class="summary-row">
                <span class="summary-label">Всего часов:</span>
                <span class="summary-value">{{ getTotalHours() }}ч</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WeekdayTimeSelector',
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        allowedTimeIntervals: {
            type: Object,
            default: () => ({})
        },
        activePeriods: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            daysOfWeek: {
                1: 'Понедельник',
                2: 'Вторник',
                3: 'Среда',
                4: 'Четверг',
                5: 'Пятница',
                6: 'Суббота',
                7: 'Воскресенье'
            },
            localTimeSettings: [],
            isInitializing: false
        };
    },
    computed: {
        availableDaysOfWeek() {
            // Получаем дни недели, которые присутствуют в периодах активности
            if (!this.activePeriods || this.activePeriods.length === 0) {
                return this.daysOfWeek; // Если периоды не заданы, показываем все дни
            }

            const availableDays = {};

            // Проходим по каждому периоду
            this.activePeriods.forEach(period => {
                if (!period.start || !period.end) return;

                const startDate = new Date(period.start);
                const endDate = new Date(period.end);

                // Проходим по каждому дню в периоде
                const currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                    const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Воскресенье = 7

                    if (this.daysOfWeek[dayOfWeek]) {
                        availableDays[dayOfWeek] = this.daysOfWeek[dayOfWeek];
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            });

            return availableDays;
        },
        dayMultipliers() {
            // Подсчитываем количество вхождений каждого дня недели во всех периодах активности
            const multipliers = {};

            if (!this.activePeriods || this.activePeriods.length === 0) {
                // Если периоды не заданы, возвращаем все дни с множителем 1
                for (let day = 1; day <= 7; day++) {
                    multipliers[day] = 1;
                }
                return multipliers;
            }

            // Инициализируем все дни с 0
            for (let day = 1; day <= 7; day++) {
                multipliers[day] = 0;
            }

            // Проходим по каждому периоду и считаем дни
            this.activePeriods.forEach(period => {
                if (!period.start || !period.end) return;

                const startDate = new Date(period.start);
                const endDate = new Date(period.end);

                // Проходим по каждому дню в периоде и считаем
                const currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                    const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Воскресенье = 7
                    if (multipliers[dayOfWeek] !== undefined) {
                        multipliers[dayOfWeek]++;
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            });

            return multipliers;
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.isInitializing = true;
            if (this.modelValue && this.modelValue.length > 0) {
                this.localTimeSettings = this.modelValue.map(ts => ({
                    day: Number(ts.day),
                    startTime: Number(ts.startTime),
                    endTime: Number(ts.endTime)
                }));
            } else {
                // Инициализируем все дни с полными 24 часами при создании
                this.initializeAllDays(true);
            }
            this.isInitializing = false;
        });
    },
    watch: {
        modelValue: {
            handler(newSettings) {
                if (this.isInitializing) return;

                const settingsChanged = JSON.stringify(newSettings || []) !== JSON.stringify(this.localTimeSettings);
                if (!settingsChanged) return;

                if (newSettings && newSettings.length > 0) {
                    this.isInitializing = true;
                    this.localTimeSettings = newSettings.map(ts => ({
                        day: Number(ts.day),
                        startTime: Number(ts.startTime),
                        endTime: Number(ts.endTime)
                    }));
                    this.isInitializing = false;
                } else {
                    // Если пришел пустой массив, инициализируем все дни с разрешенными интервалами
                    this.isInitializing = true;
                    this.initializeAllDays(false);
                    this.isInitializing = false;
                }
            },
            deep: true
        },
        allowedTimeIntervals: {
            handler() {
                // При изменении разрешенных интервалов переинициализируем, если нет текущих настроек
                if (this.localTimeSettings.length === 0) {
                    this.isInitializing = true;
                    this.initializeAllDays(false);
                    this.isInitializing = false;
                }
            },
            deep: true
        },
        activePeriods: {
            handler() {
                // При изменении периодов активности переинициализируем доступные дни
                if (this.isInitializing) return;
                
                this.$nextTick(() => {
                    this.isInitializing = true;
                    // Удаляем настройки времени для дней, которые больше не доступны
                    const availableDayNumbers = Object.keys(this.availableDaysOfWeek).map(Number);
                    this.localTimeSettings = this.localTimeSettings.filter(ts =>
                        availableDayNumbers.includes(ts.day)
                    );
                    // Добавляем настройки для новых дней
                    this.initializeAllDays(false);
                    this.isInitializing = false;
                });
            },
            deep: true
        },
        availableDaysOfWeek: {
            handler(newAvailableDays) {
                if (this.isInitializing) return;

                // Удаляем настройки времени для дней, которые больше не доступны
                const availableDayNumbers = Object.keys(newAvailableDays).map(Number);
                const filteredSettings = this.localTimeSettings.filter(ts =>
                    availableDayNumbers.includes(ts.day)
                );

                if (filteredSettings.length !== this.localTimeSettings.length) {
                    this.isInitializing = true;
                    this.localTimeSettings = filteredSettings;
                    // Добавляем настройки для новых дней
                    this.initializeAllDays(false);
                    this.isInitializing = false;
                }
            },
            deep: true
        }
    },
    methods: {
        hasTimeSettingsForDay(dayNumber) {
            const dayNum = Number(dayNumber);
            return this.localTimeSettings.some(ts => ts.day === dayNum);
        },

        getTimeSettingsForDay(dayNumber) {
            const dayNum = Number(dayNumber);
            return this.localTimeSettings.filter(ts => ts.day === dayNum);
        },

        initializeAllDays(silent = false) {
            // Инициализируем доступные дни недели с разрешенными интервалами из кампании
            const availableDayNumbers = Object.keys(this.availableDaysOfWeek).map(Number);
            availableDayNumbers.forEach(dayNumber => {
                if (!this.hasTimeSettingsForDay(dayNumber)) {
                    // Если есть разрешенные интервалы для этого дня, используем их
                    if (this.allowedTimeIntervals && this.allowedTimeIntervals[dayNumber] && this.allowedTimeIntervals[dayNumber].length > 0) {
                        // Добавляем все разрешенные интервалы
                        this.allowedTimeIntervals[dayNumber].forEach(interval => {
                            this.localTimeSettings.push({
                                day: dayNumber,
                                startTime: interval.startTime,
                                endTime: interval.endTime,
                            });
                        });
                    } else {
                        // Если нет ограничений, инициализируем с полными 24 часами
                        this.localTimeSettings.push({
                            day: dayNumber,
                            startTime: 0, // 00:00
                            endTime: 24 * 60 * 60 * 1000 // 24:00 (24 часа)
                        });
                    }
                }
            });
            if (!silent) {
                this.emitUpdate();
            }
        },

        isHourActiveForDay(dayNumber, hour) {
            const dayNum = Number(dayNumber);
            const daySettings = this.getTimeSettingsForDay(dayNum);

            if (daySettings.length === 0) {
                return false;
            }

            const hourMs = (hour - 1) * 60 * 60 * 1000;

            return daySettings.some(ts => {
                return hourMs >= ts.startTime && hourMs < ts.endTime;
            });
        },
        toggleHourForDay(dayNumber, hour) {
            const dayNum = Number(dayNumber);
            
            // Проверяем, разрешен ли этот час для выбора
            if (!this.isHourAllowedForDay(dayNum, hour)) {
                return;
            }
            
            const hourMs = (hour - 1) * 60 * 60 * 1000;
            const daySettings = this.getTimeSettingsForDay(dayNum);

            const containingInterval = daySettings.find(ts =>
                hourMs >= ts.startTime && hourMs < ts.endTime
            );

            if (containingInterval) {
                const totalActiveHours = this.getTotalHoursForDay(dayNum);
                if (totalActiveHours <= 1) {
                    return;
                }
                this.deactivateHourInInterval(dayNum, hourMs);
            } else {
                this.activateHourInInterval(dayNum, hourMs);
            }

            this.emitUpdate();
        },
        activateHourInInterval(dayNumber, hourMs) {
            const dayNum = Number(dayNumber);
            
            // Проверяем, что час разрешен для выбора
            const hour = Math.floor(hourMs / (60 * 60 * 1000)) + 1;
            if (!this.isHourAllowedForDay(dayNum, hour)) {
                return;
            }
            
            const daySettings = this.getTimeSettingsForDay(dayNum);

            this.localTimeSettings = this.localTimeSettings.filter(ts => ts.day !== dayNum);

            let merged = false;
            const newIntervals = [];

            const sortedSettings = [...daySettings].sort((a, b) => a.startTime - b.startTime);

            for (let i = 0; i < sortedSettings.length; i++) {
                const interval = sortedSettings[i];
                const nextInterval = sortedSettings[i + 1];

                if (nextInterval && hourMs === interval.endTime && hourMs === nextInterval.startTime - 60 * 60 * 1000) {
                    newIntervals.push({
                        day: dayNum,
                        startTime: interval.startTime,
                        endTime: nextInterval.endTime
                    });
                    i++;
                    merged = true;
                } else if (hourMs === interval.startTime - 60 * 60 * 1000) {
                    newIntervals.push({
                        day: dayNum,
                        startTime: hourMs,
                        endTime: interval.endTime
                    });
                    merged = true;
                } else if (hourMs === interval.endTime) {
                    newIntervals.push({
                        day: dayNum,
                        startTime: interval.startTime,
                        endTime: hourMs + 60 * 60 * 1000
                    });
                    merged = true;
                } else {
                    newIntervals.push(interval);
                }
            }

            if (!merged) {
                newIntervals.push({
                    day: dayNum,
                    startTime: hourMs,
                    endTime: hourMs + 60 * 60 * 1000
                });
            }

            this.localTimeSettings.push(...newIntervals);
        },
        deactivateHourInInterval(dayNumber, hourMs) {
            const dayNum = Number(dayNumber);
            const daySettings = this.getTimeSettingsForDay(dayNum);

            this.localTimeSettings = this.localTimeSettings.filter(ts => ts.day !== dayNum);

            daySettings.forEach(ts => {
                if (hourMs >= ts.startTime && hourMs < ts.endTime) {
                    if (hourMs === ts.startTime) {
                        if (ts.endTime > ts.startTime + 60 * 60 * 1000) {
                            this.localTimeSettings.push({
                                day: dayNum,
                                startTime: ts.startTime + 60 * 60 * 1000,
                                endTime: ts.endTime
                            });
                        }
                    } else if (hourMs === ts.endTime - 60 * 60 * 1000) {
                        if (ts.startTime < ts.endTime - 60 * 60 * 1000) {
                            this.localTimeSettings.push({
                                day: dayNum,
                                startTime: ts.startTime,
                                endTime: ts.endTime - 60 * 60 * 1000
                            });
                        }
                    } else {
                        this.localTimeSettings.push({
                            day: dayNum,
                            startTime: ts.startTime,
                            endTime: hourMs
                        });
                        this.localTimeSettings.push({
                            day: dayNum,
                            startTime: hourMs + 60 * 60 * 1000,
                            endTime: ts.endTime
                        });
                    }
                } else {
                    this.localTimeSettings.push(ts);
                }
            });
        },

        getHourFromTime(timeMs) {
            return Math.floor(timeMs / (60 * 60 * 1000));
        },

        getTotalHoursForDay(dayNumber) {
            const daySettings = this.getTimeSettingsForDay(dayNumber);
            
            if (daySettings.length === 0) {
                return 0;
            }

            // Объединяем перекрывающиеся интервалы
            const sortedSettings = [...daySettings].sort((a, b) => a.startTime - b.startTime);
            const mergedIntervals = [];
            
            for (let i = 0; i < sortedSettings.length; i++) {
                const current = sortedSettings[i];
                
                if (mergedIntervals.length === 0) {
                    mergedIntervals.push({ ...current });
                } else {
                    const last = mergedIntervals[mergedIntervals.length - 1];
                    
                    // Если интервалы перекрываются или соприкасаются, объединяем их
                    if (current.startTime <= last.endTime) {
                        last.endTime = Math.max(last.endTime, current.endTime);
                    } else {
                        mergedIntervals.push({ ...current });
                    }
                }
            }

            // Считаем общее количество часов в миллисекундах, затем переводим в часы
            let totalMs = 0;
            mergedIntervals.forEach(ts => {
                totalMs += ts.endTime - ts.startTime;
            });

            // Округляем до целых часов
            return Math.round(totalMs / (60 * 60 * 1000));
        },

        getDayMultiplier(dayNumber) {
            const dayNum = Number(dayNumber);
            return this.dayMultipliers[dayNum] || 1;
        },
        getActiveDaysCount() {
            // Считаем суммарное количество вхождений всех активных дней недели
            const activeDayNumbers = new Set(this.localTimeSettings.map(ts => ts.day));
            let totalCount = 0;

            activeDayNumbers.forEach(dayNum => {
                const multiplier = this.getDayMultiplier(dayNum);
                totalCount += multiplier;
            });

            return totalCount;
        },

        getTotalHours() {
            // Считаем общее количество часов с учетом множителей дней недели
            let totalHours = 0;
            const activeDayNumbers = new Set(this.localTimeSettings.map(ts => ts.day));

            activeDayNumbers.forEach(dayNum => {
                const dayHours = this.getTotalHoursForDay(dayNum);
                const multiplier = this.getDayMultiplier(dayNum);
                totalHours += dayHours * multiplier;
            });

            return totalHours;
        },

        formatHourRange(hour) {
            const startHour = hour - 1;
            const endHour = hour;
            return `${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`;
        },

        isHourAllowedForDay(dayNumber, hour) {
            const dayNum = Number(dayNumber);
            
            // Если нет ограничений для этого дня, разрешаем все
            if (!this.allowedTimeIntervals || !this.allowedTimeIntervals[dayNum] || this.allowedTimeIntervals[dayNum].length === 0) {
                return true;
            }
            
            const hourMs = (hour - 1) * 60 * 60 * 1000;
            const allowedIntervals = this.allowedTimeIntervals[dayNum];
            
            // Проверяем, входит ли час в один из разрешенных интервалов
            return allowedIntervals.some(interval => {
                return hourMs >= interval.startTime && hourMs < interval.endTime;
            });
        },

        getHourTooltip(dayNumber, hour) {
            const isActive = this.isHourActiveForDay(dayNumber, hour);
            const isAllowed = this.isHourAllowedForDay(dayNumber, hour);
            const hourRange = this.formatHourRange(hour);
            
            if (!isAllowed) {
                return `${hourRange} - недоступно (не выбрано в кампании)`;
            }
            
            return `${hourRange} - ${isActive ? 'активен' : 'неактивен'}`;
        },

        emitUpdate() {
            this.$emit('update:modelValue', [...this.localTimeSettings]);
        }
    }
};
</script>

<style scoped>
.weekday-time-selector {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    margin: 12px 0;
}

.selector-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
}

.selector-header h4 {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.weekdays-container {
    padding: 0;
}

.weekday-section {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.2s ease;
}

.weekday-section:last-child {
    border-bottom: none;
}

.weekday-section.has-settings {
    background: #f8fafc;
}

.weekday-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    gap: 12px;
}

.weekday-name {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 120px;
}

.day-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    user-select: none;
}

.day-multiplier {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    margin-left: 6px;
    user-select: none;
}

.hour-grid-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    margin: 0 12px;
}

.hour-labels {
    display: flex;
    gap: 2px;
    margin-bottom: 4px;
}

.hour-label {
    width: 17px;
    text-align: center;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 500;
}

.hour-grid {
    display: flex;
    gap: 2px;
    justify-content: center;
}

.hour-checkbox {
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    width: 17px !important;
    height: 17px !important;
}

.hour-checkbox:hover {
    transform: scale(1.1);
}

.checkbox-square {
    width: 100% !important;
    height: 100% !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 3px !important;
    position: relative !important;
    transition: all 0.2s ease !important;
}

.square-fill {
    width: 0% !important;
    height: 0% !important;
    background: #10b981 !important;
    border-radius: 2px !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    transition: all 0.2s ease !important;
}

.hour-checkbox.active .checkbox-square {
    border: none !important;
    background: transparent !important;
}

.hour-checkbox.active .square-fill {
    width: 100% !important;
    height: 100% !important;
}

.hour-checkbox.disabled {
    cursor: not-allowed;
    opacity: 0.3;
}

.hour-checkbox.disabled .checkbox-square {
    background-color: #f1f5f9 !important;
    border-color: #cbd5e1 !important;
}

.hour-checkbox.disabled:hover {
    transform: none;
}

.day-summary {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    min-width: 80px;
    justify-content: flex-end;
}

.total-hours {
    color: #475569;
    font-weight: 600;
}

.global-summary {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    padding: 16px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.summary-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.summary-label {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.summary-value {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
}

/* Адаптивность */
@media (max-width: 768px) {
    .selector-header {
        padding: 10px 12px;
    }

    .weekday-header {
        padding: 8px 12px;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .global-summary {
        padding: 12px;
        gap: 10px;
    }

    .summary-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
}
</style>
